import { Dialog } from "radix-ui";
import { useLayoutEffect, useState } from "react";

import { Button } from "./Button";
import styles from "./MediaLightbox.module.css";
import { Icon } from "../icons";
import { cx } from "../lib/cx";

export type MediaLightboxProps = {
  /** Detail beside the name on the caption bar — the file's size. */
  readonly caption: string;
  /**
   * What the download is saved as, which for a screenshot is not
   * {@link name} — the caption bar titles itself with the tool's heading.
   */
  readonly filename: string;
  /** A still, or something with a timeline and a play button. */
  readonly kind: "image" | "video";
  readonly name: string;
  readonly onClose: () => void;
  readonly src: string;
};

/**
 * How much of the space it is given a popped-out image should try to take up:
 * it grows until *one* axis reaches this fraction, then stops. Measured off
 * the viewport rather than the image, so every pop-out lands at about the same
 * size on screen whatever it started as (`chat.md` §Attachments).
 */
const MIN_VIEWPORT_FILL = 0.6;

/**
 * The ceiling on that growth, which binds only under about 246×161. Below
 * that a crop needs 5× or more to reach the fill target, and mush at that size
 * reads as though the file were low quality rather than small.
 */
const MAX_SCALE = 3;

/**
 * The room the viewer takes for itself: its inset from each edge, plus the
 * caption bar and the gap above it. These repeat numbers in
 * `MediaLightbox.module.css`, whose own `max-width` / `max-height` must stay
 * no *tighter* than they are — a tighter bound would letterbox the image
 * against a size this component already chose.
 */
const VIEWER_INSET = 24;
const CAPTION_SPACE = 40 + 10;

/** The intrinsic size of whichever element is showing, once it knows it. */
const intrinsicSize = (
  element: HTMLImageElement | HTMLVideoElement,
): { height: number; width: number } =>
  element instanceof HTMLVideoElement
    ? { height: element.videoHeight, width: element.videoWidth }
    : { height: element.naturalHeight, width: element.naturalWidth };

/**
 * A picture or a clip popped out over the dimmed page — the same bytes as the
 * thumbnail, sized to the viewport (`chat.md` §Attachments). A clip opens
 * playing, muted, under the browser's own controls.
 *
 * Mounted only while open, the way the runner drawer is. Radix brings Escape,
 * the focus trap and dismiss-on-outside-click; because the dialog *is* the
 * image, everything around it is overlay and a click there closes.
 */
export const MediaLightbox = ({
  caption,
  filename,
  kind,
  name,
  onClose,
  src,
}: MediaLightboxProps) => {
  // Both dimensions are computed here rather than left to `max-width` /
  // `max-height`: a cap only shrinks, and capping one axis of an explicitly
  // sized image stretches it. One scale factor on both enlarges and keeps the
  // shape.
  //
  // A layout effect rather than a `load` handler alone, because the thumbnail
  // already fetched these bytes — the element is usually decoded before React
  // attaches one, and nothing would ever fire.
  const [element, setElement] = useState<
    HTMLImageElement | HTMLVideoElement | null
  >(null);
  const [size, setSize] = useState<{
    readonly height: number;
    readonly width: number;
  } | null>(null);
  useLayoutEffect(() => {
    if (!element) {
      return;
    }
    const fit = (): void => {
      const { height: h, width: w } = intrinsicSize(element);
      if (!w || !h) {
        return;
      }
      const roomW = window.innerWidth - VIEWER_INSET * 2;
      const roomH = window.innerHeight - VIEWER_INSET * 2 - CAPTION_SPACE;
      // Shrink to fit, grow to the fill mark — and never both, since an image
      // that has to shrink is already past every target there is.
      const shrink = Math.min(roomW / w, roomH / h);
      const grow = Math.min(
        (roomW * MIN_VIEWPORT_FILL) / w,
        (roomH * MIN_VIEWPORT_FILL) / h,
        MAX_SCALE,
      );
      const scale = Math.min(shrink, Math.max(1, grow));
      setSize({ height: Math.round(h * scale), width: Math.round(w * scale) });
    };
    fit();
    // An image announces itself with `load` and a video with `loadedmetadata`.
    // Both are attached whichever is showing, so this never has to ask which.
    element.addEventListener("load", fit);
    element.addEventListener("loadedmetadata", fit);
    window.addEventListener("resize", fit);
    return () => {
      element.removeEventListener("load", fit);
      element.removeEventListener("loadedmetadata", fit);
      window.removeEventListener("resize", fit);
    };
  }, [element]);

  return (
    <Dialog.Root
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
      open
    >
      <Dialog.Portal>
        <Dialog.Overlay className={cx(styles.scrim, "u-fade")} />
        <Dialog.Content
          aria-describedby={undefined}
          className={cx(styles.viewer, "u-pop")}
        >
          {kind === "video" ? (
            <video
              aria-label={name}
              // Muted is what makes the autoplay stick: Chrome credits a
              // gesture to the element it landed on, and this one was a click
              // on a thumbnail that did not exist yet.
              autoPlay
              className={styles.media}
              controls
              muted
              playsInline
              ref={setElement}
              src={src}
              style={size ?? undefined}
            />
          ) : (
            <img
              alt={name}
              className={styles.media}
              ref={setElement}
              src={src}
              style={size ?? undefined}
            />
          )}
          <div className={styles.bar}>
            <Dialog.Title className={styles.name} title={name}>
              {name}
            </Dialog.Title>
            <span className={styles.caption}>{caption}</span>
            <a
              className={styles.download}
              download={filename}
              href={src}
              title="Download"
            >
              <Icon.ArrowDown size={14} />
            </a>
            <Dialog.Close asChild>
              <Button icon title="Close" variant="ghost">
                <Icon.X size={15} />
              </Button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
